import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { Activity } from 'lucide-react'
import { useState } from 'react'

import type { LedgerGlDailyBalanceRow } from '@/api/ledger-types'
import { session } from '@/api/session'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { branchDescription } from '@/lib/branch'
import { formatDateTime, formatShortDate } from '@/lib/datetime'

import { startOfMonth, today } from './constants'
import { useDailyBalances, useLedgerBranchReach } from './queries'
import { useDebounced } from './use-debounced'

/**
 * The stored daily balance rollups.
 *
 * One row per account, per branch, per day: what the account opened at, what moved, what it closed
 * at. These are the figures the trial balance is assembled from and the figures a drift check
 * re-derives from the postings underneath them and compares against — so this screen is where
 * somebody goes after a drift alarm to see the row that disagreed.
 *
 * The date range is required by the endpoint, which is the right constraint: an unbounded read of
 * every account on every day since the institution opened is not a screen, it is an outage. The
 * range defaults to the current month.
 *
 * Read-only, so it works at any width. Every amount is integer pesewas rendered by
 * {@link MoneyDisplay}.
 */

/** The endpoint takes no ordering parameter. */
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const ANY = '__any__'

const DEFAULT_PAGE_SIZE = 100

const helper = dataTableColumns<LedgerGlDailyBalanceRow>()

const columns: DataTableColumn<LedgerGlDailyBalanceRow>[] = helper.columns([
  helper.accessor('businessDate', {
    header: 'Date',
    enableSorting: false,
    cell: (context) => (
      <span className="numeric text-xs tabular-nums">
        {formatShortDate(context.row.original.businessDate)}
      </span>
    ),
    meta: { noWrap: true, widthClass: 'w-32', card: 'lead' },
  }),
  helper.accessor('glAccountCode', {
    header: 'Account',
    enableSorting: false,
    cell: (context) => (
      <span className="font-mono text-xs">{context.row.original.glAccountCode}</span>
    ),
    meta: { noWrap: true, widthClass: 'w-32', card: 'lead' },
  }),
  helper.accessor('glAccountName', {
    header: 'Name',
    enableSorting: false,
    cell: (context) => (
      <span className="text-sm text-heading">{context.row.original.glAccountName}</span>
    ),
    meta: { card: 'title' },
  }),
  helper.accessor('branchId', {
    header: 'Branch',
    enableSorting: false,
    cell: (context) => <span className="font-mono text-xs">Branch {context.row.original.branchId}</span>,
    meta: { noWrap: true, widthClass: 'w-28' },
  }),
  helper.accessor('openingMinorUnits', {
    header: 'Opening',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.openingMinorUnits}
        currency={context.row.original.currencyCode}
        symbol={false}
        negativeStyle="parentheses"
      />
    ),
    meta: { align: 'right', widthClass: 'w-36', cardLabel: 'Opening' },
  }),
  helper.accessor('debitMinorUnits', {
    header: 'Debits',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.debitMinorUnits}
        currency={context.row.original.currencyCode}
        symbol={false}
      />
    ),
    meta: { align: 'right', widthClass: 'w-36', cardLabel: 'Debits' },
  }),
  helper.accessor('creditMinorUnits', {
    header: 'Credits',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.creditMinorUnits}
        currency={context.row.original.currencyCode}
        symbol={false}
      />
    ),
    meta: { align: 'right', widthClass: 'w-36', cardLabel: 'Credits' },
  }),
  helper.accessor('closingMinorUnits', {
    header: 'Closing',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.closingMinorUnits}
        currency={context.row.original.currencyCode}
        symbol={false}
        negativeStyle="parentheses"
        emphasis="strong"
      />
    ),
    meta: { align: 'right', widthClass: 'w-36', cardLabel: 'Closing' },
  }),
  helper.accessor('lastUpdatedAtUtc', {
    header: 'Rolled up',
    enableSorting: false,
    // When the rollup was last written, which is the question after a drift repair: has this row
    // been rewritten since the alarm, or is it still the figure that disagreed?
    cell: (context) => (
      <span className="numeric text-2xs tabular-nums text-muted">
        {formatDateTime(context.row.original.lastUpdatedAtUtc)}
      </span>
    ),
    meta: { noWrap: true, card: 'hidden' },
  }),
])

export function DailyBalancesSection() {
  const reach = useLedgerBranchReach()

  const [fromDate, setFromDate] = useState(() => startOfMonth(today()))
  const [toDate, setToDate] = useState(today)
  const [glAccountCode, setGlAccountCode] = useState('')
  const [branchId, setBranchId] = useState<string | null>(() => session.getBranchId())
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const rangeValid = fromDate !== '' && toDate !== '' && fromDate <= toDate

  // Settled before it reaches the query key — see `use-debounced.ts`.
  const settledAccountCode = useDebounced(glAccountCode.trim())

  const query = useDailyBalances(
    { fromDate, toDate, glAccountCode: settledAccountCode, branchId, page, pageSize },
    rangeValid,
  )

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater

    setPage(next.pageIndex + 1)
    setPageSize(next.pageSize)
  }

  return (
    <div className="space-y-4">
      <Alert tone="info">
        <Activity aria-hidden />
        <div className="space-y-1">
          <AlertTitle>These are the stored figures, not a recomputation</AlertTitle>
          <AlertDescription>
            Each row is the rollup the ledger holds for one account on one day at one branch. A
            balance drift check re-derives the same figure from the postings underneath it and alarms
            where the two disagree — so when an alarm names a row, this is where to read it.
          </AlertDescription>
        </div>
      </Alert>

      <div className="grid gap-3 rounded-card border border-border-subtle bg-surface p-3 lg:grid-cols-4">
        <div className="space-y-1.5">
          <Label htmlFor="balances-from">From</Label>
          <Input
            id="balances-from"
            type="date"
            value={fromDate}
            onChange={(event) => {
              setFromDate(event.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="balances-to">To</Label>
          <Input
            id="balances-to"
            type="date"
            value={toDate}
            onChange={(event) => {
              setToDate(event.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="balances-account">GL account</Label>
          <Input
            id="balances-account"
            value={glAccountCode}
            autoComplete="off"
            spellCheck={false}
            className="font-mono"
            placeholder="Every account"
            onChange={(event) => {
              setGlAccountCode(event.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="balances-branch">Branch</Label>
          <Select
            value={branchId ?? ANY}
            onValueChange={(next) => {
              setBranchId(next === ANY ? null : next)
              setPage(1)
            }}
          >
            <SelectTrigger id="balances-branch">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Every branch you can reach</SelectItem>
              {reach.branches.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>
                  {branchDescription(branch)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="lg:col-span-4">
          <p className="text-xs text-muted">
            The range is required by the endpoint, and rightly: every account on every day since the
            institution opened is not a page. Narrow it to one account to follow a single balance
            through the month.
          </p>
          {!rangeValid ? (
            <p className="mt-1 text-xs text-danger-text">
              {fromDate === '' || toDate === ''
                ? 'Both dates are needed.'
                : 'The start of the range is after its end.'}
            </p>
          ) : null}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={query.data?.items}
        rowCount={query.data?.totalCount}
        pagination={{ pageIndex: page - 1, pageSize }}
        onPaginationChange={onPaginationChange}
        sorting={UNSORTED}
        onSortingChange={ignoreSortingChange}
        isLoading={rangeValid && query.isPending}
        isFetching={query.isFetching}
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
        getRowId={(row) => `${row.branchId}:${row.businessDate}:${row.glAccountCode}`}
        pageSizeOptions={[50, DEFAULT_PAGE_SIZE, 200]}
        emptyTitle={rangeValid ? 'No rollups in that range' : 'Choose a date range'}
        emptyDescription={
          rangeValid
            ? 'The ledger holds no daily balance for those dates, that account, or that branch. A day with no postings still has a rollup once it has been closed — an absent one is worth a drift check.'
            : 'The stored rollups are read over a bounded range. Set both dates above.'
        }
        emptyAction={
          rangeValid ? null : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setFromDate(startOfMonth(today()))
                setToDate(today())
              }}
            >
              Use this month
            </Button>
          )
        }
      />
    </div>
  )
}
